'use client'

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { DiscoverType } from '@/hooks/useDiscoverUrlFilters'

interface DiscoverFilterTypeChoicesProps {
  onSelect: (discoverType: DiscoverType) => void
}

const typeChoiceButtonClassName =
  'group w-full rounded-2xl border border-border/70 bg-card text-left shadow-sm transition-colors hover:bg-card/95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-[0.99]'

const skeletonBlockClassName = 'bg-muted/60'

function TypeChoiceLabel({ children }: { children: ReactNode }) {
  return (
    <div className="card-content flex items-center justify-center px-2">
      <span className="text-sm font-medium text-foreground">{children}</span>
    </div>
  )
}

function FeedItemArtistNameSkeleton() {
  return (
    <div className="card-content flex items-center justify-center gap-2 px-2">
      <div className={`h-6 w-6 shrink-0 rounded-full ${skeletonBlockClassName}`} />
      <div className={`h-4 w-16 rounded-md ${skeletonBlockClassName}`} />
    </div>
  )
}

function FeedItemChoiceSkeleton({
  label,
  showFlashPills = false,
}: {
  label: string
  showFlashPills?: boolean
}) {
  return (
    <div className="flex flex-col p-2">
      <div
        className={`relative w-full rounded-[var(--card-radius)] shadow-lg ${skeletonBlockClassName}`}
        style={{ aspectRatio: '400/532' }}
      >
        {showFlashPills ? (
          <>
            <div className="absolute left-2 top-2 h-6 w-12 rounded-full bg-background/90" />
            <div className="absolute right-2 top-2 h-6 w-16 rounded-full bg-background/90" />
          </>
        ) : null}
      </div>
      <FeedItemArtistNameSkeleton />
      <TypeChoiceLabel>{label}</TypeChoiceLabel>
    </div>
  )
}

function ArtistChoiceSkeleton({ label }: { label: string }) {
  return (
    <div className="p-2">
      <div className="flex items-center gap-3">
        <div className={`h-16 w-16 shrink-0 rounded-2xl border-4 border-border md:h-20 md:w-20 ${skeletonBlockClassName}`} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className={`h-5 w-28 rounded-md ${skeletonBlockClassName}`} />
          <div className={`h-4 w-20 rounded-md ${skeletonBlockClassName}`} />
        </div>
      </div>

      <div className="mt-2 grid w-full min-w-0 grid-cols-3 gap-1.5">
        {[0, 1, 2].map((index) => (
          <div key={index} className={`aspect-square min-h-0 w-full rounded-xl ${skeletonBlockClassName}`} />
        ))}
      </div>

      <TypeChoiceLabel>{label}</TypeChoiceLabel>
    </div>
  )
}

export function DiscoverFilterTypeChoices({ onSelect }: DiscoverFilterTypeChoicesProps) {
  const { t } = useTranslation()

  const flashesLabel = t('navigation.flashes', 'Flashes')
  const tattoosLabel = t('navigation.tattoos', 'Tattoos')
  const artistsLabel = t('navigation.artists', 'Artists')

  return (
    <div className="flex flex-col gap-4 py-5">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          className={typeChoiceButtonClassName}
          onClick={() => onSelect('flashes')}
        >
          <FeedItemChoiceSkeleton label={flashesLabel} showFlashPills />
        </button>

        <button
          type="button"
          className={typeChoiceButtonClassName}
          onClick={() => onSelect('tattoos')}
        >
          <FeedItemChoiceSkeleton label={tattoosLabel} />
        </button>
      </div>

      <button
        type="button"
        className={typeChoiceButtonClassName}
        onClick={() => onSelect('artists')}
      >
        <ArtistChoiceSkeleton label={artistsLabel} />
      </button>
    </div>
  )
}
